import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { useWS } from "../../context/WebSocketContext";
import NotificationsDropdown from "./NotificationsDropdown";
import TagFilterDropdown from "./TagFilterDropdown";
import HelpTooltip from "./HelpTooltip";
import api from "../../lib/api";

export default function Navbar({
  title, subtitle, actions, onOpenLeadFromNotification,
  // Filtro de tags do kanban -- opcional, só o Dashboard passa esses props.
  // Nas demais páginas (Tasks, Settings, etc.) fica omitido e nada muda.
  showTagFilter, selectedTags, onTagsChange,
}) {
  const { connected } = useWS();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchBoxRef = useRef(null);

  // Busca de leads por nome/telefone/email/empresa. Roda em qualquer etapa,
  // inclusive Ganho/Perdido (que ficam ocultas por padrão no kanban) — o
  // backend ignora exclude_column_ids de propósito quando há termo de busca.
  // Quando o filtro de tags do kanban está ativo, a busca também respeita
  // essas tags (mesmo `selectedTags` usado pra filtrar o board) -- assim os
  // dois filtros funcionam em conjunto também aqui, não só no board.
  useEffect(() => {
    const term = query.trim();
    if (!term) {
      setResults([]);
      setShowResults(false);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      let url = `/leads?search=${encodeURIComponent(term)}`;
      if (Array.isArray(selectedTags) && selectedTags.length > 0) {
        url += `&tags=${encodeURIComponent(selectedTags.join(","))}`;
      }
      api.get(url)
        .then(({ data }) => {
          setResults(Array.isArray(data) ? data.slice(0, 8) : []);
          setShowResults(true);
        })
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query, selectedTags]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const openResult = (lead) => {
    setQuery("");
    setResults([]);
    setShowResults(false);
    // Mesmo mecanismo já usado pelo botão "Abrir chat" em /tasks: navega pro
    // Dashboard, que busca o lead pelo id e abre o ChatPanel. Funciona a
    // partir de qualquer página (Navbar é compartilhada).
    navigate("/", { state: { openLeadId: lead.id } });
  };

  return (
    <header
      data-testid="app-navbar"
      className="h-16 px-6 flex items-center justify-between bg-slate-900/80 backdrop-blur-md border-b border-slate-800"
    >
      <div className="flex items-center gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-50" style={{ fontFamily: "Outfit" }}>
            {title}
          </h1>
          {subtitle && <p className="text-xs text-slate-400 -mt-0.5">{subtitle}</p>}
        </div>
        <span
          data-testid="ws-status"
          className={`ml-3 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border ${
            connected ? "border-[#25D366]/40 text-[#25D366] bg-[#25D366]/10" : "border-slate-700 text-slate-500"
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-[#25D366]" : "bg-slate-600"}`} />
          {connected ? "Ao vivo" : "Offline"}
        </span>
      </div>

      <div className="flex items-center gap-3">
        {showTagFilter && (
          <div data-testid="tag-filter-wrapper" className="flex items-center gap-1">
            <TagFilterDropdown selected={selectedTags} onChange={onTagsChange} />
            <HelpTooltip text="Filtra o funil pra mostrar só leads que tenham pelo menos uma das tags selecionadas (lógica OU) -- as mesmas tags usadas no card do lead." />
          </div>
        )}
        <div className="relative" ref={searchBoxRef}>
          {searching ? (
            <Loader2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 animate-spin" />
          ) : (
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          )}
          <input
            data-testid="global-search"
            placeholder="Buscar leads..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.trim() && setShowResults(true)}
            className="bg-slate-800/60 border border-slate-700 text-sm text-slate-100 placeholder-slate-500 pl-9 pr-3 py-2 rounded-lg w-64 focus:outline-none focus:border-[#25D366]/60 focus:bg-slate-800 transition"
          />
          {showResults && (
            <div
              data-testid="global-search-results"
              className="absolute right-0 mt-2 w-80 max-h-80 overflow-y-auto bg-slate-900 border border-slate-700 rounded-lg shadow-2xl z-50"
            >
              {results.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-slate-500">
                  {searching ? "Buscando..." : "Nenhum lead encontrado"}
                </div>
              ) : (
                results.map((lead) => (
                  <button
                    key={lead.id}
                    data-testid={`global-search-result-${lead.id}`}
                    onClick={() => openResult(lead)}
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-800 border-b border-slate-800 last:border-0 transition"
                  >
                    <div className="text-sm font-medium text-slate-100 truncate">{lead.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {[lead.company, lead.phone || lead.display_phone].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        <NotificationsDropdown onOpenLead={onOpenLeadFromNotification} />
        {actions}
      </div>
    </header>
  );
}
